import { companiesService } from '@/features/properties/services/companies.service';
import { PARTNER_CLASSIFICATIONS } from '@/lib/constants';
import { SearchOption } from '@/types/search';
import { propertiesService } from '@/features/properties/services/properties.service';
import { FunctionName, PermissionAction } from '@/lib/enums';

/**
 * Company option interface for autocomplete
 */

/**
 * Cache for company options to avoid repeated API calls
 */
const optionsCache: {
  plantOptions: SearchOption[] | null;
  witnessCompanyOptions: SearchOption[] | null;
} = {
  plantOptions: null,
  witnessCompanyOptions: null,
};

/**
 * Get plan name options (プランド - Supplier Type 3)
 * Partner classification = 2 (Supplier) with supplierType = 3
 * @param functionName - Function name
 * @returns Promise with array of SearchOption
 */
export const getPlanNameOptions = async (
  functionName: FunctionName,
  permissionAction?: PermissionAction
): Promise<SearchOption[]> => {
  if (optionsCache.plantOptions) {
    return optionsCache.plantOptions;
  }

  try {
    // Fetch suppliers with supplierType = 3 (Plant/プランド)
    const companies = await companiesService.getCompaniesByClassificationAndType(
      PARTNER_CLASSIFICATIONS.SUPPLIER,
      3, // supplierType = 3 for Plant
      functionName,
      permissionAction ?? PermissionAction.READ
    );

    // Transform to CompanyOption format
    const options = companies.map((company: { id: string; name: string }) => ({
      key: company.id,
      value: company.name,
      label: company.name,
    }));

    optionsCache.plantOptions = options as SearchOption[];
    return options as SearchOption[];
  } catch (error) {
    console.error('Error fetching plan name options:', error);
    return [];
  }
};

/**
 * Get witness company options (立会会社)
 * Partner classification = 4
 * @returns Promise with array of SearchOption
 */
export const getWitnessCompanyOptions = async (
  functionName: FunctionName,
  permissionAction?: PermissionAction
): Promise<SearchOption[]> => {
  if (optionsCache.witnessCompanyOptions) {
    return optionsCache.witnessCompanyOptions;
  }

  try {
    // Fetch witness companies (classification = 4)
    const companies = await companiesService.getCompaniesByClassification(
      PARTNER_CLASSIFICATIONS.WITNESS_COMPANY,
      functionName,
      permissionAction ?? PermissionAction.READ
    );

    // Transform to CompanyOption format
    const options = companies.map((company: { id: string; name: string }) => ({
      key: company.id,
      value: company.name,
      label: company.name,
    }));

    optionsCache.witnessCompanyOptions = options as SearchOption[];
    return options as SearchOption[];
  } catch (error) {
    console.error('Error fetching witness company options:', error);
    return [];
  }
};

/**
 * Find company name by id
 */
export const getCompanyNameById = (
  id: string | number | null | undefined,
  options: SearchOption[]
): string => {
  if (!id) return '';
  const company = options.find((opt) => opt.key === String(id));
  return (company?.label as string) || '';
};

/**
 * Find company id by name
 * Uses exact match first, then case-insensitive match
 */
export const getCompanyIdByName = (
  name: string | null | undefined,
  options: SearchOption[]
): string | null => {
  if (!name) return null;

  // Trim whitespace
  const trimmedName = name.trim();

  // Try exact match first
  let company = options.find((opt) => opt.label === trimmedName);

  // If not found, try case-insensitive match
  if (!company) {
    company = options.find((opt) => opt.label.trim().toLowerCase() === trimmedName.toLowerCase());
  }

  return company?.key as string | null;
};

/**
 * Get property name options from API (現場)
 * @param hasArchived - Include archived properties
 * @param functionName - Function name
 * @returns Promise with array of SearchOption
 */
export const getPropertyOptions = async (
  functionName: FunctionName,
  hasArchived: boolean = false,
  permissionAction?: PermissionAction
): Promise<SearchOption[]> => {
  try {
    // Fetch property names from API
    const properties = await propertiesService.getPropertyNames({
      hasArchived: hasArchived,
      functionName: functionName,
      ...(permissionAction && { permissionAction }),
    });

    // Transform to SearchOption format (no default empty option for autocomplete)
    const options = properties.map((property) => ({
      key: property.id,
      value: property.name,
      label: property.name,
    }));

    return [{ key: '', value: '', label: '指定なし' }, ...options];
  } catch (error) {
    console.error('Error fetching property options:', error);
    return [{ key: '', value: '', label: '指定なし' }];
  }
};
